/**
 * TextField — `Input` + RHF `Controller` + label + error message.
 *
 * One of the field components code-patterns §20 names as living in
 * `@flint/ui/forms`. Feature components compose `<TextField name="legalName"
 * label="Company name" />` instead of re-wiring RHF every time.
 */
import * as React from "react";
import { useFormContext, type FieldPath, type FieldValues } from "react-hook-form";
import { Input, type InputProps } from "../primitives/input.js";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "./form.js";

export interface TextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<InputProps, "name" | "defaultValue"> {
  name: TName;
  label: React.ReactNode;
  /** Helper text under the input. Hidden when an error is showing. */
  helper?: React.ReactNode;
}

export function TextField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, label, helper, ...inputProps }: TextFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField<TFieldValues, TName>
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...inputProps} {...field} value={field.value ?? ""} />
          </FormControl>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
